import { Box, Button, CircularProgress, Dialog, Typography, useMediaQuery } from '@mui/material';
import axios from 'axios';
import { useState } from 'react';

function ContactUs({
  isContactUsOpened,
  setContactUsOpened,
}: {
  isContactUsOpened: boolean;
  setContactUsOpened: (s: boolean) => void;
}) {
  const isMobile = useMediaQuery('(max-width:800px)');
  const isHeightLess700 = useMediaQuery('(max-height:700px)');
  const [isLoading, setLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [isEmailSent, setEmailSent] = useState(false);
  const DEFAULT_VALUES = { name: '', email: '', message: '' };
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [errors, setErrors] = useState<{ name: string | boolean; email: string | boolean }>({
    name: false,
    email: false,
  });

  const handleClose = () => {
    setContactUsOpened(false);
    setErrors({ name: false, email: false });
  };

  const handleSend = () => {
    const isValidEmail = String(values.email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
    const errors = {
      name: values.name === '' ? 'This field is required' : false,
      email:
        values.email === '' ? 'This field is required' : isValidEmail === null ? 'Please enter a valid email' : false,
    };
    setErrors(errors);

    if (!Object.values(errors).some((val) => val !== false)) {
      setLoading(true);
      setLoadingError(false);

      axios
        .post('https://growstats.io/remote-mailer', {
          from: values.email,
          subject: values.name,
          body: values.message,
        })
        .then(function (response) {
          console.log('response', response);
          setEmailSent(true);
        })
        .catch(function (error) {
          console.log('fetch remote-mailer err', error);
          setLoadingError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      fullScreen={isMobile}
      open={isContactUsOpened}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? undefined : '30px',
          '&::-webkit-scrollbar': {
            width: 0,
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        },
      }}
    >
      <Box
        sx={{
          padding: isMobile ? '30px 20px' : isHeightLess700 ? '20px 50px' : '50px',
          borderRadius: '30px',
          height: '100%',
        }}
      >
        {isEmailSent ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'RedHat700',
                fontSize: isMobile ? '32px' : '40px',
                color: '#171717',
                lineHeight: 'normal',
                marginBottom: isMobile ? '10px' : '15px',
                textAlign: 'center',
              }}
            >
              Thank you
            </Typography>
            <Typography
              sx={{
                fontFamily: 'RedHat400',
                fontSize: isMobile ? '16px' : '20px',
                color: '#656769',
                lineHeight: isMobile ? 'normal' : '30px',
                textAlign: 'center',
              }}
            >
              Our managers will contact you as soon as possible!
            </Typography>
            <Button
              sx={{
                padding: isMobile ? '15px 30px' : '20px 40px',
                borderRadius: '12px',
                background: '#247BFE',
                color: '#FFF',
                fontFamily: 'RedHat800',
                fontSize: isMobile ? '16px' : '20px',
                lineHeight: 'normal',
                transition: 'all 0.3s ease',
                minWidth: '270px',
                width: '270px',
                marginTop: '30px',
                textTransform: 'initial',
                '&:hover': {
                  opacity: 0.8,
                },
                '& .MuiButton-startIcon': {
                  marginRight: isMobile ? '10px' : '20px',
                },
              }}
              onClick={handleClose}
            >
              Got it
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <img
              onClick={handleClose}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: isMobile ? '20px' : '30px',
                right: isMobile ? '20px' : '30px',
              }}
              src="/images/close.svg"
              alt="close"
            ></img>
            <Typography
              sx={{
                fontFamily: 'RedHat700',
                fontSize: isMobile ? '32px' : '40px',
                color: '#171717',
                lineHeight: 'normal',
                marginBottom: isMobile ? '10px' : '15px',
              }}
            >
              Contact us
            </Typography>
            <Typography
              sx={{
                fontFamily: 'RedHat400',
                fontSize: isMobile ? '16px' : '20px',
                color: '#656769',
                lineHeight: isMobile ? 'normal' : '30px',
              }}
            >
              Share your contact information, and our team will reach out to discuss your product purchase in detail.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                padding: '30px 0 ',
                '& .title': {
                  fontFamily: 'RedHat400',
                  fontSize: '16px',
                  color: '#656769',
                  lineHeight: 'normal',
                },
                '& input, textarea': {
                  resize: 'none',
                  cursor: 'pointer',
                  fontFamily: 'RedHat400',
                  fontSize: isMobile ? '18px' : '20px',
                  lineHeight: '30px',
                  color: '#171717',
                  borderRadius: '12px',
                  border: '1px solid #CFD6E0',
                  background: 'transparent',
                  padding: '12px 20px',
                  transition: 'all 0.3s ease',
                  width: isMobile ? undefined : 'calc(100% - 40px)',
                  '&:hover': {
                    background: 'transparent',
                    transition: 'all 0.3s ease',
                  },
                  '&:focus': {
                    background: 'transparent',
                    outline: 'none',
                  },
                },
                '& .error': {
                  color: '#F42144',
                  fontFamily: 'RedHat400',
                  fontSize: '12px',
                  lineHeight: 'normal',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '20px',
                }}
              >
                <Box display={'flex'} gap="8px" flexDirection={'column'}>
                  <Typography className="title">Your name</Typography>
                  <input
                    value={values.name}
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, name: e.target.value }));
                      setErrors((prev) => ({ ...prev, name: false }));
                    }}
                    style={{
                      border: errors.name ? '1px solid #F42144' : undefined,
                    }}
                    type="text"
                  ></input>
                  <Typography
                    sx={{
                      display: errors.name ? undefined : 'none',
                    }}
                    className="error"
                  >
                    {errors.name}
                  </Typography>
                </Box>
                <Box display={'flex'} gap="8px" flexDirection={'column'}>
                  <Typography className="title">Email</Typography>
                  <input
                    value={values.email}
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, email: e.target.value }));
                      setErrors((prev) => ({ ...prev, email: false }));
                    }}
                    style={{
                      border: errors.email ? '1px solid #F42144' : undefined,
                    }}
                    type="email"
                  ></input>
                  <Typography
                    sx={{
                      display: errors.email ? undefined : 'none',
                    }}
                    className="error"
                  >
                    {errors.email}
                  </Typography>
                </Box>
              </Box>
              <Box display={'flex'} gap="8px" flexDirection={'column'}>
                <Typography className="title">Your message</Typography>
                <textarea
                  value={values.message}
                  onChange={(e) => {
                    setValues((prev) => ({ ...prev, message: e.target.value }));
                  }}
                  rows={isHeightLess700 ? 1 : 3}
                ></textarea>
              </Box>
            </Box>
            {loadingError && (
              <Typography
                sx={{
                  color: '#F42144',
                  fontFamily: 'RedHat400',
                  fontSize: '12px',
                  lineHeight: 'normal',
                  textAlign: isMobile ? 'center' : 'left',
                  marginBottom: '10px',
                }}
              >
                Something went wrong
              </Typography>
            )}
            <Button
              sx={{
                padding: isMobile ? '15px 30px' : '20px 40px',
                borderRadius: '12px',
                background: '#247BFE',
                color: '#FFF',
                fontFamily: 'RedHat800',
                fontSize: isMobile ? '16px' : '20px',
                lineHeight: 'normal',
                transition: 'all 0.3s ease',
                minWidth: isMobile ? '100%' : '270px',
                textTransform: 'initial',
                // width: '100%',
                '&:hover': {
                  opacity: 0.8,
                },
                '& .MuiButton-startIcon': {
                  marginRight: isMobile ? '10px' : '20px',
                },
              }}
              onClick={handleSend}
            >
              {isLoading ? (
                <CircularProgress
                  size={26}
                  sx={{
                    color: '#FFF',
                    // maxHeight: '16px'
                  }}
                ></CircularProgress>
              ) : (
                'Send'
              )}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

export default ContactUs;
